import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FileFormat, RelatedToType } from '@shared/enums';
import {
  FilterListItemValueModel,
  TsButtonModule,
  TsPageComponent,
  TsPageHeaderModule,
  TsPaginatorChangeEvent,
  TSTreeMenuModule,
  TSTreeNode,
} from 'tsui';
import { TableData, Entity, ActionType } from '../types';
import { SavedView } from '@core/models';
import { BehaviorSubject, filter, forkJoin, map, noop, Observable, of, switchMap, tap } from 'rxjs';
import { JupiterState } from '../@store/state';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { TsComponent } from '../@components/ts.component';
import { JupiterDataTableComponent } from './@components/data-table/data-table.component';
import { JupiterActionBarComponent } from './@components/action-bar/action-bar.component';
import { JupiterSavedViewAddComponent } from './@components/view-form/view-form.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ofActionSuccessful } from '@ngxs/store';
import { LabelSlotPipe } from '../@pipes/label-slot.pipe';
import { LabelSlot, PermissionSlot } from '../@store/types';
import { CustomFormOptions } from '@shared/modules/custom-form/custom-form.types';
import { CustomFormComponent } from '@shared/modules/custom-form/custom-form/custom-form.component';
import { DirectivesModule } from '@shared/directives/directives.module';
import { PermissionSlotPipe } from '../@pipes/permission-slot.pipe';
import { MassUpdateTagsDialog } from '@shared/modals/mass-update-tags/mass-update-tags.component';
import { TableActions } from 'tsui/lib/cross-cutting/models/table-actions';
import { AddEngagementDialogComponent } from 'app/modules/engagements/modals/add-engagement/add-engagement.component';
import { ColumnMovedEvent, SortChangedEvent, SortDirection } from 'ag-grid-enterprise';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { FieldDefsState } from '@state/fields/state';
import { EntityActions, TemplateActions, ViewActions } from '@jupiter-actions';
import { Permissionable } from '@modules/projects/projects.types';
import { TsPageSidenavConfig } from 'tsui/lib/ts-page/ts-page.component';

@Component({
  selector: 'jupiter-home',
  standalone: true,
  imports: [
    TsPageComponent,
    TsButtonModule,
    MatTooltipModule,
    TSTreeMenuModule,
    TsPageHeaderModule,
    MatProgressBarModule,
    AsyncPipe,
    JupiterDataTableComponent,
    JupiterActionBarComponent,
    LabelSlotPipe,
    DirectivesModule,
    PermissionSlotPipe,
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss', '../../../shared/styles/ts-page-header-list.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JupiterHomeComponent extends TsComponent implements OnInit, OnDestroy {
  filterValueList = new BehaviorSubject<FilterListItemValueModel[]>([]);
  tagsList = new BehaviorSubject<string[]>([]);
  search = new BehaviorSubject<string>('');
  data$: Observable<TableData<any>> = this.store.select(JupiterState.getData);
  view$: Observable<SavedView> = this.store.select(JupiterState.getView).pipe(
    tap((view) => {
      if (view) {
        this.isViewDataReady = false;
        this.setupViewData(view);
      }
    }),
  );
  featureSupport$ = this.store.select(JupiterState.getFeatureSupport);
  isViewDataReady = false;

  private setupViewData(view: SavedView): void {
    const viewDefinition = JSON.parse(view.definition);
    this.displayColumns = viewDefinition?.table?.fields ?? [];
    this.activeSort = viewDefinition?.sort ?? null;

    const filterDefinition = JSON.parse(view.saved_filter.filters);
    this.filterValueList.next(this.fjsonStringToFilterValue(filterDefinition?.fjson ?? '[]'));
    this.tagsList.next(filterDefinition?.tags ?? []);

    this.currentPaginatorData = {
      activePage: 1,
      pageSize: 20,
    };

    this.isViewDataReady = true;
  }

  views$: Observable<TSTreeNode[]> = this.store.select(JupiterState.getViews).pipe(
    tap(() => this.tsSnackbarService.dismiss()),
    map(this.mapViewsToTsTreeNodes.bind(this)),
  );

  relatedTo$ = this.store.select(JupiterState.getRelatedTo);

  fieldDefs$ = this.store.select(JupiterState.getRelatedTo).pipe(
    switchMap((relatedTo) => {
      return this.store.select(
        ObjectsManagerState.getFieldsByObjectName(relatedTo, {
          allowedFieldsOnly: true,
          addNestedFields: true,
        }),
      );
    }),
    map((fieldDefs) => {
      if (!fieldDefs) {
        return [];
      }

      return this.tsFilterUtilsService.getFilterList(fieldDefs);
    }),
  );

  displayColumns: string[] = [];
  activeSort: { active: string; direction: SortDirection } = null;
  tagsCounter = 0;
  selectedRows: Entity[] = [];
  isLoading = false;
  sideNavConfig: TsPageSidenavConfig;
  gridActions$: Observable<TableActions[]> = this.featureSupport$.pipe(
    switchMap((featureSupport) => {
      if (!featureSupport) {
        return of([]);
      }

      const actions: TableActions<Entity>[] = [];

      if (!featureSupport.gridActions.enabled) {
        return of(actions);
      }

      featureSupport.gridActions.actions.logCall &&
        actions.push({
          label: 'Log Call',
          action: (item) => this.addCompanyEngagementModal('Call', item),
          icon: 'space_dashboard',
        });

      featureSupport.gridActions.actions.logMeeting &&
        actions.push({
          label: 'Log Meeting',
          action: (item) => this.addCompanyEngagementModal('Meeting', item),
          icon: 'edit',
        });

      featureSupport.gridActions.actions.logSocialMedia &&
        actions.push({
          label: 'Log Social Media',
          action: (item) => this.addCompanyEngagementModal('Social Media', item),
          icon: 'edit',
        });

      featureSupport.gridActions.actions.openDetails &&
        actions.push({
          label: 'Open Details',
          action: (item) => this.onGoToDetails(item),
          icon: 'space_dashboard',
        });

      featureSupport.gridActions.actions.edit &&
        actions.push({
          label: 'Edit',
          action: (item) => {
            this.onEdit(item);
          },
          icon: 'edit',
          isDisabled: (item: any) => {
            const { data } = item as { data: Permissionable };
            return !data.permissions.is_editable;
          },
        });

      featureSupport.gridActions.actions.delete &&
        actions.push({
          label: 'Delete',
          action: (item) => {
            this.confirmationDialogService
              .openConfirmationDialog({
                dialogTitle: 'Delete Confirmation',
                description: `This <b>${this.store.selectSnapshot(JupiterState.getRelatedToObject).label}</b> will be deleted.`,
                renderHtml: true,
                buttonsConfig: {
                  yes: 'Confirm',
                  no: 'Cancel',
                },
              })
              .pipe(filter((result: boolean) => result))
              .subscribe(() => this.store.dispatch(new EntityActions.Delete(item.id)));
          },
          icon: 'delete',
          isDisabled: (item: any) => {
            const { data } = item as { data: Permissionable };
            return !data.permissions.is_deletable;
          },
        });

      return of(actions);
    }),
  );

  LabelSlot = LabelSlot;
  PermissionSlot = PermissionSlot;

  private isSaveViewPromptOpen = false;
  private currentPaginatorData: Partial<TsPaginatorChangeEvent> = { activePage: 1, pageSize: 20 };

  private get hasContext(): boolean {
    return !!this.activatedRoute.snapshot.data.context;
  }

  constructor() {
    super();

    this.sideNavConfig = {
      title: 'Saved Views',
      actions: [],
    };

    if (this.can(new PermissionSlotPipe().transform(PermissionSlot.PUBLIC_CUSTOM_VIEWS))) {
      this.sideNavConfig.actions = [
        ...this.sideNavConfig.actions,
        {
          actionFn: () => {
            this.matDialog
              .open(JupiterSavedViewAddComponent, {
                data: { saved_views: this.store.selectSnapshot(JupiterState.getViews) },
              })
              .afterClosed()
              .pipe(filter((result) => !!result))
              .subscribe((result: { name: string }) => {
                this.store.dispatch(
                  new ViewActions.AddView({
                    name: result.name,
                    related_to_type: this.store.selectSnapshot(JupiterState.getRelatedTo),
                    definition: JSON.stringify({ table: { fields: this.displayColumns } }),
                  }),
                );
              });
          },
          description: 'Add new saved view',
          icon: 'add_circle_outline',
        },
      ];
    }
  }

  delegateLeafClickFn(route: string): void {
    this.isViewDataReady = false;
    this.search.next('');
    this.router.navigateByUrl(route);
  }

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params: { relatedToKey; viewId; recordId; tabId }) => {
        if (!this.store.selectSnapshot(JupiterState.getRelatedTo)) {
          this.router.navigateByUrl(`/space/void?return=${this.router.url}`, { skipLocationChange: true });
          return;
        } else {
          const tempViewId = this.store.selectSnapshot(JupiterState.getView)?.id;

          this.store.dispatch(new ViewActions.SetView(params.viewId)).subscribe(() => {
            if (
              (this.router.getCurrentNavigation()?.extras?.info?.['betweenMoons'] === true &&
                !Object.keys(this.activatedRoute.snapshot.queryParams ?? {}).length) ||
              tempViewId !== Number(params.viewId)
            ) {
              this.dispatchSearch();
            }
          });
        }
      });

    this.activatedRoute.queryParams
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((params: { page: number }) => !!this.store.selectSnapshot(JupiterState.getRelatedTo)),
        tap((params: { page: number }) => {
          if (!this.isViewDataReady) {
            this.setupViewData(this.store.selectSnapshot(JupiterState.getView));
          }

          if (
            this.store.selectSnapshot(JupiterState.getView).id !== Number(this.activatedRoute.snapshot.params.viewId)
          ) {
            return;
          }

          this.dispatchSearch({ page: Number(params.page ?? '1') });
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionSuccessful(ViewActions.AddView, ViewActions.DuplicateView),
        tap(() => {
          const viewsSize = this.store.selectSnapshot(JupiterState.getViews).length;
          const lastView = this.store.selectSnapshot(JupiterState.getViews)[viewsSize - 1];
          this.router.navigate(['..', lastView.id], { relativeTo: this.activatedRoute });
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionSuccessful(ViewActions.DeleteView),
        tap(() => {
          const [firstView] = this.store.selectSnapshot(JupiterState.getViews);
          this.router.navigate(['..', firstView.id], { relativeTo: this.activatedRoute });
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionSuccessful(EntityActions.Add),
        tap(() => {
          this.layoutService.closeComponentDrawer();
          if (this.store.selectSnapshot(JupiterState.getContext)) {
            this.dispatchSearch();
          } else {
            const entity = this.store.selectSnapshot(JupiterState.getEntity);
            this.router.navigate([entity.id], { relativeTo: this.activatedRoute });
          }
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionSuccessful(EntityActions.Update),
        tap(() => {
          this.dispatchSearch();
          this.layoutService.closeComponentDrawer();
          this.store.dispatch(new EntityActions.ClearTransient());
        }),
      )

      .subscribe(noop);

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionSuccessful(EntityActions.Delete),
        tap(() => this.dispatchSearch()),
      )
      .subscribe(noop);
  }

  ngOnDestroy(): void {
    this.store.dispatch(new EntityActions.ClearData());
    this.tsSnackbarService.dismiss();
  }

  onPageChanged(event: TsPaginatorChangeEvent): void {
    this.currentPaginatorData = event;
    this.router.navigate([], {
      queryParams: {
        page: event.activePage,
      },
    });
  }

  onColumnSelectionChanged(event: string[]): void {
    this.promptSaveViewSnackbar();
    this.displayColumns = event;
    this.dispatchSearch();
  }

  onSortChanged(event: SortChangedEvent): void {
    const [column] = event.columns;
    const newActiveSort = { active: column.getColDef().field, direction: column.getSort() };

    if (this.activeSort?.active === newActiveSort.active && this.activeSort?.direction === newActiveSort.direction) {
      return;
    }

    if (!newActiveSort.direction) {
      this.activeSort = null;
    } else {
      this.activeSort = newActiveSort;
    }

    this.promptSaveViewSnackbar();
    this.dispatchSearch();
  }

  onColumnMoved(event: ColumnMovedEvent): void {
    const columns = event.api.getColumns().map((x) => x.getColId());
    const oldIndex = columns.findIndex((x) => x === event.column.getColId());
    const newIndex = event.toIndex;

    moveItemInArray(columns, oldIndex, newIndex);
    this.displayColumns = columns.filter((x) => !['openDetails', 'checkbox', 'actions'].includes(x));
    this.promptSaveViewSnackbar();
  }

  onTagsUpdated(event: string[]): void {
    this.promptSaveViewSnackbar();
    this.tagsList.next(event);
    this.currentPaginatorData = { activePage: 1, pageSize: this.currentPaginatorData.pageSize };
    this.dispatchSearch();
  }

  onFilterApplied(event: FilterListItemValueModel[]): void {
    this.promptSaveViewSnackbar();
    this.filterValueList.next(event);
    this.currentPaginatorData = { activePage: 1, pageSize: this.currentPaginatorData.pageSize };

    if (this.activatedRoute.snapshot.queryParams.page) {
      this.router.navigate([], { queryParams: {} });
    } else {
      this.dispatchSearch();
    }
  }

  onSearchChanged(event: string): void {
    this.search.next(event);
    this.currentPaginatorData = { activePage: 1, pageSize: this.currentPaginatorData.pageSize };

    if (this.activatedRoute.snapshot.queryParams.page) {
      this.router.navigate([], { queryParams: {} });
    } else {
      this.router.navigate([], { queryParams: { page: 1 } });
    }
  }

  onCellDataChanged(data: Entity) {
    this.store.dispatch(new EntityActions.Update(data));
  }

  onRowSelectionChanged(data: Entity[]) {
    this.selectedRows = data;
  }

  onExportAsCsv(): void {
    this.store.dispatch(
      new EntityActions.Export(
        {
          connection: this.store.selectSnapshot(JupiterState.getRelatedTo) as unknown as RelatedToType,
          fields: this.displayColumns,
          filter: {
            fjson: this.filterValueToFJSONString(this.filterValueList.value),
            queryObject: {
              saved_view_id: this.store.selectSnapshot(JupiterState.getView).id,
            },
          },
        },
        {
          savedViewId: String(this.store.selectSnapshot(JupiterState.getView).id),
          format: FileFormat.CSV,
          fileName: 'FileName-ChangeIt',
        },
      ),
    );
  }

  onEdit(entity: Entity): void {
    this.onAdd(entity);
  }

  onAdd(initialValue: Entity = undefined): void {
    forkJoin([
      this.accountService.getStandardDropdowns(),
      initialValue ? this.store.dispatch(new EntityActions.LoadTransient(initialValue.id)) : of(null),
    ]) // this needs to change
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(([options]) => {
          this.layoutService.openComponentDrawer<CustomFormOptions>(CustomFormComponent, {
            inputs: {
              initialValue:
                this.store.selectSnapshot(JupiterState.getTransientEntity) ??
                this.store.selectSnapshot(JupiterState.getRelationEntityValue),
              relatedToType: this.store.selectSnapshot(JupiterState.getRelatedTo) as RelatedToType,
              renderAttachFileField: false,
              allowedFields: this.store.selectSnapshot(
                ObjectsManagerState.getFieldsByObjectName(this.store.selectSnapshot(JupiterState.getRelatedTo), {
                  allowedFieldsOnly: true,
                  addNestedFields: false,
                }),
              ),
              selectedFields: this.store.selectSnapshot(JupiterState.getAddFormDefinition()),
              dataSources: {
                template_id: this.store
                  .selectSnapshot(FieldDefsState.selectDropdownOptionsBy('project_templates'))
                  .filter(
                    (x) => x.related_to_type === this.store.selectSnapshot(JupiterState.getRelatedTo) && !x.hidden,
                  ),
              },
              commonData: {
                formFieldData: { options: { ...options, pipelines: [], users: [] }, users: [], categories: [] },
                disableItemSuggestion: true,
              },
              saveAction: ((data: any) => {
                if (initialValue) {
                  this.store
                    .dispatch(new EntityActions.Update(data))
                    .pipe(
                      takeUntilDestroyed(this.destroyRef),
                      tap(() => {}),
                    )
                    .subscribe(noop);
                } else {
                  this.store
                    .dispatch(new EntityActions.Add(data))
                    .pipe(
                      takeUntilDestroyed(this.destroyRef),
                      tap(() => {}),
                    )
                    .subscribe(noop);
                }
              }).bind(this),
              enableEditing: true,
              canEditLayout: this.can(
                this.store.selectSnapshot(JupiterState.getPermissionSlot(PermissionSlot.MANAGE_ADD_FORM)),
              ),
            },
            outputs: {
              onTemplateChanged: (fields) => {
                this.store.dispatch(new TemplateActions.UpdateAddForm(fields));
              },
              onNavigationByUrlRequested: (url: string) => {
                // this._router.navigateByUrl(url);
              },
            },
          });
        }),
      )
      .subscribe(noop);
  }

  onGoToDetails(entity: Entity): void {
    // TODO: uncomment this when we have the permissions for all objects
    // if (!entity.permissions.is_viewable) {
    //   return;
    // }

    if (this.store.selectSnapshot(JupiterState.getContext)) {
      const key = this.store.selectSnapshot(JupiterState.getRelatedToKey);
      const viewId = this.store.selectSnapshot(JupiterState.getView).id;

      this.router.navigateByUrl(`/space/void?return=/space/${key}/${viewId}/${entity.id}`, {
        skipLocationChange: true,
      });

      return;
    }

    this.store.dispatch(new EntityActions.ClearData());
    this.router.navigate([entity.id], { relativeTo: this.activatedRoute });
  }

  onOpenTagsBulkUpdate(): void {
    this.matDialog
      .open(MassUpdateTagsDialog, {
        maxWidth: '50vw',
        width: '100%',
        maxHeight: '80vh',
        height: 'auto',
        data: {
          related_to_type: this.store.selectSnapshot(JupiterState.getRelatedTo),
          selected_items_id: this.selectedRows.map((x) => x.id),
        },
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe(() => {
        this.currentPaginatorData = { activePage: 1, pageSize: this.currentPaginatorData.pageSize };
        this.dispatchSearch();
      });
  }

  onViewActionTriggered(event: ActionType): void {
    switch (event) {
      case 'edit':
        this.matDialog
          .open(JupiterSavedViewAddComponent, {
            data: {
              name: this.store.selectSnapshot(JupiterState.getView).name,
              saved_views: this.store.selectSnapshot(JupiterState.getViews),
            },
          })
          .afterClosed()
          .pipe(filter((result) => !!result))
          .subscribe((result: { name: string }) => {
            this.store.dispatch(new ViewActions.UpdateViewName(result.name));
          });
        break;
      case 'duplicate':
        this.store.dispatch(new ViewActions.DuplicateView());
        break;
      case 'delete':
        this.store.dispatch(new ViewActions.DeleteView());
        break;
    }
  }

  private dispatchSearch(pagination: { page: number; size?: number } = null): void {
    const filter = {
      query: this.search.value,
      tags: this.tagsList.value.join(','),
      fjson: this.filterValueToFJSONString(this.filterValueList.value),
      pagination: {
        page: pagination?.page ?? this.currentPaginatorData.activePage,
        size: pagination?.size ?? this.currentPaginatorData.pageSize,
      },
    };

    if (this.activeSort) {
      filter['sort'] = [{ field: this.activeSort.active, order: this.activeSort.direction }];
    }

    this.store.dispatch(
      new EntityActions.Search({
        connection: this.store.selectSnapshot(JupiterState.getRelatedTo) as unknown as RelatedToType,
        fields: this.displayColumns,
        filter,
      }),
    );
  }

  private promptSaveViewSnackbar(): void {
    if (this.isSaveViewPromptOpen) {
      return;
    }

    this.isSaveViewPromptOpen = true;

    this.tsSnackbarService.openApplyChanges(
      (() => {
        this.isSaveViewPromptOpen = false;

        this.store.dispatch(new ViewActions.SetView(this.activatedRoute.snapshot.params.viewId));

        if (this.activatedRoute.snapshot.queryParams.page) {
          this.router.navigate([], { queryParams: {} });
        } else {
          this.dispatchSearch();
        }
      }).bind(this),
      (() => {
        this.isSaveViewPromptOpen = false;

        const definition = { table: { fields: this.displayColumns } };

        if (this.activeSort) {
          definition['sort'] = this.activeSort;
        }

        this.store.dispatch(
          new ViewActions.UpdateView(
            JSON.stringify(definition),
            JSON.stringify({
              fjson: this.filterValueToFJSONString(this.filterValueList.value),
              tags: this.tagsList.value,
            }),
          ),
        );
      }).bind(this),
    );
  }

  private filterValueToFJSONString(filterValue: FilterListItemValueModel[]): string {
    return `[${filterValue.map((x) => `["${x.fieldName}", "${x.operator}", ${JSON.stringify(x.value)}]`).join(', ')}]`;
  }

  private fjsonStringToFilterValue(fjson: string): FilterListItemValueModel[] {
    return JSON.parse(fjson ?? '[]').map(([fieldName, operator, value]) => ({
      fieldName,
      operator,
      value,
    })) as FilterListItemValueModel[];
  }

  private addCompanyEngagementModal(engagement_type: string, entity: Entity): void {
    this.accountService
      .getStandardDropdowns() // this needs to change
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((options) => {
          return this.matDialog
            .open(AddEngagementDialogComponent, {
              data: {
                options,
                engagement_type: engagement_type,
                [this.store.selectSnapshot(JupiterState.getRelatedTo).toLowerCase()]: entity,
              },
            })
            .afterClosed();
        }),
      )
      .subscribe(noop);
  }

  private mapViewsToTsTreeNodes(views: SavedView[]): TSTreeNode[] {
    return views.map((view) => {
      let link = `/space/${this.store.selectSnapshot(ObjectsManagerState.getUrlPrefixByObjectName(view.related_to_type))}/${view.id}`;
      if (this.hasContext) {
        link = `/${this.router.url
          .split('/')
          .filter((x) => x)
          .reverse()
          .map((x, i) => (i === 0 ? view.id : x))
          .reverse()
          .join('/')}`.replace(
          /\/moon\/co_\d+.*\//,
          `/moon/${this.store.selectSnapshot(ObjectsManagerState.getUrlPrefixByObjectName(view.related_to_type))}/`,
        ); // I don't like this, it's just a glue for now
      }
      return {
        id: String(view.id),
        name: view.name,
        icon: 'view_list',
        link,
      };
    });
  }
}
